// Cookie management utilities for form persistence
// Usage: import { setCookie, getCookie, deleteCookie } from '@/utils/cookieUtils'

/**
 * Set a cookie with enhanced error handling and verification
 * @param name Cookie name
 * @param value Cookie value
 * @param days Number of days until expiration (default: 30)
 */
export function setCookie(name: string, value: string, days: number = 30): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expiresDate = date.toUTCString();

    // Include SameSite=Lax for modern browser compatibility
    const cookieString = `${name}=${encodeURIComponent(value)}; expires=${expiresDate}; path=/; SameSite=Lax`;
    document.cookie = cookieString;

    // comentado para nao ficar mais exibindo msg quando o conteudo do cookie for vazio
    // Verify cookie was actually set - this catches intermittent failures
    //const verification = getCookie(name);
    // if (verification !== value) {
    //   console.warn(
    //     `[Cookie WARN] ${name} set but verification failed. Expected: "${value}", Got: "${verification}"`,
    //   );
    // }
  } catch (error) {
    console.error(`[Cookie ERROR] Failed to set ${name}:`, error);
  }
}

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  try {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(cookie.substring(nameEQ.length));
        return value || null;
      }
    }
    return null;
  } catch (error) {
    console.error(`[Cookie ERROR] Failed to get ${name}:`, error);
    return null;
  }
}

/**
 * Delete a cookie by setting its expiration to the past
 * @param name Cookie name
 */
export function deleteCookie(name: string): void {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
  } catch (error) {
    console.error(`[Cookie ERROR] Failed to delete ${name}:`, error);
  }
}

/**
 * Delete multiple cookies at once
 * @param names Array of cookie names to delete
 */
export function deleteCookies(names: string[]): void {
  names.forEach((name) => deleteCookie(name));
}

/**
 * Clear all cookies matching a pattern (prefix)
 * @param prefix Cookie name prefix to match
 * @example clearCookiesByPrefix("form_") // deletes all cookies starting with "form_"
 */
export function clearCookiesByPrefix(prefix: string): void {
  try {
    const cookies = document.cookie.split(";");
    cookies.forEach((cookie) => {
      const cookieName = cookie.trim().split("=")[0];
      if (cookieName && cookieName.startsWith(prefix)) {
        deleteCookie(cookieName);
      }
    });
  } catch (error) {
    console.error(`[Cookie ERROR] Failed to clear cookies with prefix ${prefix}:`, error);
  }
}

// Clear every cookie currently stored for the current site.
export function clearAllSiteCookies(): void {
  try {
    const cookies = document.cookie.split(";");
    cookies.forEach((cookie) => {
      const cookieName = cookie.trim().split("=")[0];
      if (cookieName) {
        deleteCookie(cookieName);
      }
    });
  } catch (error) {
    console.error("[Cookie ERROR] Failed to clear site cookies:", error);
  }
}
